import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ip_back } from "../config";
import AgentMark from "../digiping/AgentMark";
import DigipingHostMark from "../digiping/DigipingHostMark";
import smartttDefaultLogo from "../assets/smarttt-default-logo.png";
import { resolveClubImageUrl } from "../utils/resolveClubImageUrl";
import { useBrand } from "../context/BrandContext";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useWizardViewportHeight } from "../hooks/useWizardViewportHeight";
import "./ParentProfilingWizard.css";
import "./AIProfilingWizard.css";

const STEPS = {
  PARENT_NAME: "parent_name",
  PARENT_EMAIL: "parent_email",
  PARENT_PASSWORD: "parent_password",
  PARENT_PHONE: "parent_phone",
  PARENT_RELATIONSHIP: "parent_relationship",
  EXISTING_CHILDREN: "existing_children",
  CLUB_SELECT: "club_select",
  CHILD_NAME: "child_name",
  CHILD_DOB: "child_dob",
  COMPANION_REVEAL: "companion_reveal",
  CHILD_MOTIVATION: "child_motivation",
  CHILD_EXPERIENCE: "child_experience",
  CHILD_HAND_GRIP: "child_hand_grip",
  PARENT_CONSENT: "parent_consent",
  MULTI_CHILD_CHECK: "multi_child_check",
  SUBMITTING: "submitting",
};

const EDITABLE_STEPS = new Set([
  STEPS.PARENT_NAME,
  STEPS.PARENT_EMAIL,
  STEPS.PARENT_PASSWORD,
  STEPS.PARENT_PHONE,
  STEPS.PARENT_RELATIONSHIP,
  STEPS.CLUB_SELECT,
  STEPS.CHILD_NAME,
  STEPS.CHILD_DOB,
  STEPS.CHILD_MOTIVATION,
  STEPS.CHILD_EXPERIENCE,
  STEPS.CHILD_HAND_GRIP,
  STEPS.PARENT_CONSENT,
]);

const PARENT_IDENTITY_STEPS = new Set([
  STEPS.PARENT_NAME,
  STEPS.PARENT_EMAIL,
  STEPS.PARENT_PASSWORD,
  STEPS.PARENT_PHONE,
  STEPS.PARENT_RELATIONSHIP,
  STEPS.CLUB_SELECT,
]);

const CHILD_ANSWER_STEPS = new Set([
  STEPS.CHILD_NAME,
  STEPS.CHILD_DOB,
  STEPS.CHILD_MOTIVATION,
  STEPS.CHILD_EXPERIENCE,
  STEPS.CHILD_HAND_GRIP,
  STEPS.PARENT_CONSENT,
]);

const COUNTRY_FLAGS = {
  LU: "🇱🇺",
  FR: "🇫🇷",
  BE: "🇧🇪",
  DE: "🇩🇪",
  ES: "🇪🇸",
  PT: "🇵🇹",
  NL: "🇳🇱",
  IT: "🇮🇹",
  UK: "🇬🇧",
  GB: "🇬🇧",
  US: "🇺🇸",
};

const getCountryFlag = (countryCode) => {
  if (!countryCode) return "🏓";
  const upper = String(countryCode).trim().toUpperCase();
  if (COUNTRY_FLAGS[upper]) return COUNTRY_FLAGS[upper];
  if (upper.includes("LUX")) return "🇱🇺";
  if (upper.includes("FRA")) return "🇫🇷";
  if (upper.includes("BEL")) return "🇧🇪";
  if (upper.includes("GER") || upper.includes("ALL")) return "🇩🇪";
  if (upper.includes("SPA") || upper.includes("ESP")) return "🇪🇸";
  if (upper.includes("POR")) return "🇵🇹";
  if (upper.includes("NED") || upper.includes("HOL") || upper.includes("PAY")) return "🇳🇱";
  return "🏓";
};

const getApiUrl = (endpoint) => {
  const base = (ip_back || "").toString().replace(/\/+$/, "");
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
};

export default function ParentProfilingWizard() {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const chatScrollRef = useRef(null);
  useWizardViewportHeight();

  // Helper to ensure dynamic tokens ({{firstName}}, {{name}}) are always interpolated reliably
  const translateMsg = (key, params = {}, defaultText = "") => {
    let msg = t(key, { ...params, defaultValue: defaultText });
    if (typeof msg === "string") {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          const reg = new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, "g");
          msg = msg.replace(reg, String(v));
        }
      });
    }
    return msg;
  };

  // Parent form state
  const [parentForm, setParentForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    relationship: "",
  });

  // Current child form state
  const [currentChild, setCurrentChild] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "boy",
    purpose: "fun",
    experience: "beginner",
    hand: "Right",
    grip: "Shakehand",
    consentVideo: false,
    consentPublic: false,
    consentHr: false,
    guardianAck: false,
    medicalNotes: "",
  });

  // Multi-child accumulator
  const [registeredChildren, setRegisteredChildren] = useState([]);

  // Club selection & filter
  const [clubs, setClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState("");
  const [clubSearchTerm, setClubSearchTerm] = useState("");
  const [invitationToken, setInvitationToken] = useState("");
  const [inviteClubLocked, setInviteClubLocked] = useState(false);
  const clubsRef = useRef([]);
  const selectedClubIdRef = useRef("");
  const inviteClubLockedRef = useRef(false);

  // Chat conversation
  const [messages, setMessages] = useState([
    {
      id: "msg_1",
      sender: "ai",
      agentId: "pingflo",
      text: t(
        "parentWizardIntro",
        "Hello! I am Pingflo, your SmartTT Club host. Welcome to our family onboarding! I'm here to help you register your child for table tennis and set up your Parent Progress Hub. Let's start with you — what is your first and last name?"
      ),
    },
  ]);

  const [currentStep, setCurrentStep] = useState(STEPS.PARENT_NAME);
  const [textInputValue, setTextInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  /** When set, parent is correcting a past answer for this step. */
  const [editingStep, setEditingStep] = useState(null);

  const [existingLinkedChildren, setExistingLinkedChildren] = useState([]);
  const [isReturningParent, setIsReturningParent] = useState(false);
  const [editingChildPlayerId, setEditingChildPlayerId] = useState(null);
  const [clubsReady, setClubsReady] = useState(false);

  const inviteBootRef = useRef(false);
  const returningParentBootRef = useRef(false);

  const hasInviteToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Boolean(params.get("invitation_token") || params.get("token"));
  }, [location.search]);

  const invitedClub = useMemo(() => {
    if (!selectedClubId) return null;
    return clubs.find((c) => String(c.club_id) === String(selectedClubId)) || null;
  }, [clubs, selectedClubId]);

  const invitedClubLogo =
    resolveClubImageUrl(invitedClub?.club_image) ||
    resolveClubImageUrl(brand?.logo_url) ||
    resolveClubImageUrl(brand?.logo_light_url);
  const invitedClubName =
    invitedClub?.club_name || brand?.brand_name || brand?.club_name || "";

  // Clear HTML boot overlay after invite splash can show club + shield.
  useEffect(() => {
    if (!hasInviteToken) {
      document.body.setAttribute("data-digiping-ready", "1");
      return () => document.body.removeAttribute("data-digiping-ready");
    }
    if (!clubsReady) return undefined;
    const t = setTimeout(() => {
      document.body.setAttribute("data-digiping-ready", "1");
    }, 400);
    return () => {
      clearTimeout(t);
      document.body.removeAttribute("data-digiping-ready");
    };
  }, [hasInviteToken, clubsReady]);

  // Prefill from invitation query params and skip name/email steps when already known.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("invitation_token") || params.get("token");
    const emailParam = (params.get("email") || "").trim();
    const firstNameParam = (params.get("first_name") || params.get("firstName") || "").trim();
    const lastNameParam = (params.get("last_name") || params.get("lastName") || "").trim();

    if (tokenParam) setInvitationToken(tokenParam);
    if (emailParam || firstNameParam || lastNameParam) {
      setParentForm((prev) => ({
        ...prev,
        email: emailParam || prev.email,
        firstName: firstNameParam || prev.firstName,
        lastName: lastNameParam || prev.lastName,
      }));
    }

    if (inviteBootRef.current) return;
    // Full identity from invite → jump to password; otherwise keep asking only what's missing.
    if (firstNameParam && emailParam) {
      inviteBootRef.current = true;
      const displayName = [firstNameParam, lastNameParam].filter(Boolean).join(" ");
      setMessages([
        {
          id: "msg_1",
          sender: "ai",
          agentId: "pingflo",
          text: translateMsg(
            "parentWizardInviteWelcome",
            {
              firstName: firstNameParam,
              name: firstNameParam,
              email: emailParam,
              displayName,
            },
            `Hello ${displayName}! I am Pingflo. Your club already invited you as ${emailParam}. Please choose a secure password (at least 6 characters) so you can log into your Parent Hub.`
          ),
        },
      ]);
      setCurrentStep(STEPS.PARENT_PASSWORD);
      return;
    }
    if (firstNameParam && !emailParam) {
      inviteBootRef.current = true;
      setMessages([
        {
          id: "msg_1",
          sender: "ai",
          agentId: "pingflo",
          text: translateMsg(
            "parentWizardAskEmail",
            { firstName: firstNameParam, name: firstNameParam },
            `Nice to meet you, ${firstNameParam}! What is your email address? We will use this to send you session updates and progress reports.`
          ),
        },
      ]);
      setCurrentStep(STEPS.PARENT_EMAIL);
    }
    // Invite bootstrap intentionally omits translateMsg/t from deps (run once per URL).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once from invite URL
  }, [location.search]);

  // Load available clubs (invite links resolve the invited club explicitly —
  // the general list is capped at 100 and often omits the target club).
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const clubParam = (params.get("club") || params.get("club_id") || params.get("clubId") || "").trim();
        const clubCodeParam = (params.get("club_code") || params.get("clubCode") || "").trim();
        const hasInviteClub = Boolean(clubParam || clubCodeParam);

        const qs = new URLSearchParams();
        if (clubParam) qs.set("club_id", clubParam);
        if (clubCodeParam) qs.set("club_code", clubCodeParam);
        const url = getApiUrl(`/api/parent/clubs${qs.toString() ? `?${qs}` : ""}`);
        const res = await axios.get(url);
        if (res.data?.clubs) {
          const list = res.data.clubs;
          setClubs(list);
          clubsRef.current = list;

          if (hasInviteClub && list.length > 0) {
            const found =
              list.find(
                (c) =>
                  (clubParam &&
                    (String(c.club_id) === String(clubParam) ||
                      String(c.club_code || "").toLowerCase() === String(clubParam).toLowerCase())) ||
                  (clubCodeParam &&
                    String(c.club_code || "").toLowerCase() === String(clubCodeParam).toLowerCase())
              ) || list[0];
            if (found) {
              setSelectedClubId(found.club_id);
              selectedClubIdRef.current = found.club_id;
              setInviteClubLocked(true);
              inviteClubLockedRef.current = true;
              return;
            }
          }

          if (list.length > 0 && !params.get("invitation_token") && !params.get("token")) {
            setSelectedClubId(list[0].club_id);
            selectedClubIdRef.current = list[0].club_id;
          }
        }
      } catch (err) {
        console.warn("Could not load clubs list:", err.message);
      } finally {
        setClubsReady(true);
      }
    };
    fetchClubs();
  }, [location.search]);

  useEffect(() => {
    clubsRef.current = clubs;
  }, [clubs]);

  useEffect(() => {
    selectedClubIdRef.current = selectedClubId;
  }, [selectedClubId]);

  useEffect(() => {
    inviteClubLockedRef.current = inviteClubLocked;
  }, [inviteClubLocked]);

  // Scroll latest AI question into view inside the chat panel (mobile-friendly)
  useEffect(() => {
    const scrollToLatestAi = () => {
      const container = chatScrollRef.current;
      if (!container) return;
      const aiRows = container.querySelectorAll('.chat-row.ai, .chat-row[data-sender="ai"]');
      const lastAi = aiRows.length ? aiRows[aiRows.length - 1] : null;
      if (lastAi) {
        const top =
          lastAi.offsetTop - Math.max(24, container.clientHeight - lastAi.offsetHeight - 32);
        container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    };
    requestAnimationFrame(() => {
      setTimeout(scrollToLatestAi, 80);
    });
  }, [messages, currentStep]);

  // Returning parent: skip identity if already logged in with linked children
  useEffect(() => {
    if (returningParentBootRef.current) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    let user;
    try {
      user = JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return;
    }

    const isParent = Number(user?.type_user_id) === 5 || user?.hasParentRole === true;
    if (!isParent) return;

    returningParentBootRef.current = true;

    const fetchExistingChildren = async () => {
      try {
        const res = await axios.get(getApiUrl("/api/parent/children"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const children = res.data?.children || [];
        if (children.length === 0) {
          returningParentBootRef.current = false;
          return;
        }

        setExistingLinkedChildren(children);
        setIsReturningParent(true);
        setParentForm((prev) => ({
          ...prev,
          firstName: user.user_first_name || user.firstName || prev.firstName,
          lastName: user.user_last_name || user.lastName || prev.lastName,
          email: user.user_email || user.email || prev.email,
        }));

        if (user.club_id) {
          setSelectedClubId(user.club_id);
          selectedClubIdRef.current = user.club_id;
        }

        const childNames = children.map((c) => c.first_name).filter(Boolean).join(", ");
        setMessages([
          {
            id: "msg_returning",
            sender: "ai",
            agentId: "pingflo",
            text: translateMsg(
              "parentWizardExistingChildren",
              { childNames, count: children.length },
              `Welcome back! You already have ${children.length} child${children.length > 1 ? "ren" : ""} linked (${childNames}). Would you like to update a profile or add another child?`
            ),
          },
        ]);
        setCurrentStep(STEPS.EXISTING_CHILDREN);
      } catch (err) {
        console.warn("Could not load existing children:", err.message);
        returningParentBootRef.current = false;
      }
    };

    fetchExistingChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once for returning parent
  }, []);

  // Append a message to conversation. Pass `step` on user answers to enable Edit.
  const addMessage = (sender, text, agentId = "pingflo", extra = null, step = null) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `msg_${Date.now()}_${Math.random()}`,
        sender,
        text,
        agentId,
        extra,
        ...(sender === "user" && step ? { step } : {}),
      },
    ]);
  };

  const draftForStep = (step, parent = parentForm, child = currentChild) => {
    switch (step) {
      case STEPS.PARENT_NAME:
        return [parent.firstName, parent.lastName].filter(Boolean).join(" ");
      case STEPS.PARENT_EMAIL:
        return parent.email || "";
      case STEPS.PARENT_PASSWORD:
        return "";
      case STEPS.PARENT_PHONE:
        return parent.phone || "";
      case STEPS.CHILD_NAME:
        return [child.firstName, child.lastName].filter(Boolean).join(" ");
      case STEPS.CHILD_DOB:
        return child.dateOfBirth || "";
      default:
        return "";
    }
  };

  /** Re-open a past answer: truncate chat from that bubble and show the step tray again. */
  const beginEditAnswer = (msgId, step) => {
    if (!step || !EDITABLE_STEPS.has(step) || loading || currentStep === STEPS.SUBMITTING) return;
    if (inviteClubLocked && step === STEPS.CLUB_SELECT) return;

    let nextParent = parentForm;
    let nextChild = currentChild;

    if (PARENT_IDENTITY_STEPS.has(step)) {
      setRegisteredChildren([]);
    } else if (CHILD_ANSWER_STEPS.has(step) && currentStep === STEPS.MULTI_CHILD_CHECK && registeredChildren.length > 0) {
      const popped = registeredChildren[registeredChildren.length - 1];
      nextChild = { ...currentChild, ...popped };
      setRegisteredChildren((prev) => prev.slice(0, -1));
      setCurrentChild(nextChild);
    }

    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.id === msgId);
      if (idx < 0) return prev;
      return prev.slice(0, idx);
    });

    setEditingStep(step);
    setCurrentStep(step);
    setTextInputValue(draftForStep(step, nextParent, nextChild));
    setErrorMsg("");
  };

  const finishEditingIfNeeded = () => {
    if (editingStep) setEditingStep(null);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const getRelationshipLabel = (rel) => {
    const labels = {
      father: translateMsg("parentWizardRelFather", {}, "Father"),
      mother: translateMsg("parentWizardRelMother", {}, "Mother"),
      tutor: translateMsg("parentWizardRelTutor", {}, "Tutor/Guardian"),
    };
    return labels[rel] || "Parent";
  };

  const buildChildPayload = (child) => ({
    firstName: child.firstName,
    lastName: child.lastName,
    dateOfBirth: child.dateOfBirth,
    gender: child.gender,
    purpose: child.purpose,
    experience: child.experience,
    hand: child.hand,
    grip: child.grip,
    consentVideo: !!child.consentVideo,
    consentPublic: !!child.consentPublic,
    consentHr: !!child.consentHr,
    medicalNotes: child.medicalNotes,
    relationship: child.relationship || parentForm.relationship || "parent",
    clubId: selectedClubId,
  });

  const mapApiChildToWizard = (apiChild) => ({
    firstName: apiChild.first_name || "",
    lastName: apiChild.last_name || "",
    dateOfBirth: apiChild.date_of_birth
      ? String(apiChild.date_of_birth).slice(0, 10)
      : "",
    gender: apiChild.gender || "boy",
    purpose: "fun",
    experience: "beginner",
    hand: apiChild.player_hand || "Right",
    grip: apiChild.player_grip || "Shakehand",
    consentVideo: !!apiChild.consent_video_analysis,
    consentPublic: !!apiChild.consent_public_image,
    consentHr: !!apiChild.consent_hr_data,
    guardianAck: true,
    medicalNotes: apiChild.medical_notes || "",
  });

  const continueAfterParentContact = async () => {
    const goToChildName = (selectedClub) => {
      const clubLabel = selectedClub?.club_name || "Selected Club";
      const country = selectedClub?.club_country || selectedClub?.country || "";
      const flag = getCountryFlag(country || selectedClub?.country_code);
      const fullClubStr = country ? `${flag} ${clubLabel} (${country})` : `${flag} ${clubLabel}`;
      addMessage(
        "user",
        `Joining: ${fullClubStr}`,
        "pingflo",
        null,
        inviteClubLockedRef.current ? null : STEPS.CLUB_SELECT
      );
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskChildName",
          {},
          "Wonderful! Now let's talk about your young table tennis star! What is your child's first name and last name?"
        )
      );
      setCurrentStep(STEPS.CHILD_NAME);
    };

    const resolveInviteClub = async () => {
      let clubId = selectedClubIdRef.current;
      let selectedClub = clubsRef.current.find((c) => String(c.club_id) === String(clubId));
      if (selectedClub && (inviteClubLockedRef.current || invitationToken)) {
        return selectedClub;
      }

      const params = new URLSearchParams(location.search);
      const clubParam = (params.get("club") || params.get("club_id") || params.get("clubId") || "").trim();
      const clubCodeParam = (params.get("club_code") || params.get("clubCode") || "").trim();
      if (!clubParam && !clubCodeParam) return null;

      try {
        const qs = new URLSearchParams();
        if (clubParam) qs.set("club_id", clubParam);
        if (clubCodeParam) qs.set("club_code", clubCodeParam);
        const res = await axios.get(getApiUrl(`/api/parent/clubs?${qs}`));
        const list = res.data?.clubs || [];
        if (list.length) {
          setClubs(list);
          clubsRef.current = list;
          selectedClub = list[0];
          setSelectedClubId(selectedClub.club_id);
          selectedClubIdRef.current = selectedClub.club_id;
          setInviteClubLocked(true);
          inviteClubLockedRef.current = true;
          return selectedClub;
        }
      } catch (err) {
        console.warn("Could not resolve invite club:", err.message);
      }
      return null;
    };

    const invitedClub = await resolveInviteClub();
    if (invitedClub) {
      goToChildName(invitedClub);
      return;
    }

    addMessage(
      "ai",
      translateMsg(
        "parentWizardAskClub",
        {},
        "Which table tennis club will your child be joining?"
      )
    );
    setCurrentStep(STEPS.CLUB_SELECT);
  };

  // Helper to calculate child age
  const getChildAge = (dobString) => {
    if (!dobString) return 8;
    const dob = new Date(dobString);
    const diff = Date.now() - dob.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25)));
  };

  // Handlers for steps
  const handleParentNameSubmit = () => {
    const trimmed = textInputValue.trim();
    if (!trimmed) return;
    const parts = trimmed.split(" ");
    const firstName = parts[0] || trimmed;
    const lastName = parts.slice(1).join(" ") || "";
    const knownEmail = parentForm.email;
    setParentForm((prev) => ({ ...prev, firstName, lastName }));
    finishEditingIfNeeded();

    addMessage("user", trimmed, "pingflo", null, STEPS.PARENT_NAME);
    setTextInputValue("");

    setTimeout(() => {
      if (knownEmail) {
        addMessage(
          "ai",
          translateMsg(
            "parentWizardAskPassword",
            {},
            "Great! Please choose a secure password (at least 6 characters) so you can log into your Parent Hub at any time."
          )
        );
        setCurrentStep(STEPS.PARENT_PASSWORD);
        return;
      }
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskEmail",
          { firstName, name: firstName },
          `Nice to meet you, ${firstName}! What is your email address? We will use this to send you session updates and progress reports.`
        )
      );
      setCurrentStep(STEPS.PARENT_EMAIL);
    }, 450);
  };

  const handleParentEmailSubmit = () => {
    const email = textInputValue.trim();
    if (!email || !email.includes("@")) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setErrorMsg("");
    setParentForm((prev) => ({ ...prev, email }));
    finishEditingIfNeeded();

    addMessage("user", email, "pingflo", null, STEPS.PARENT_EMAIL);
    setTextInputValue("");

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskPassword",
          {},
          "Great! Please choose a secure password (at least 6 characters) so you can log into your Parent Hub at any time."
        )
      );
      setCurrentStep(STEPS.PARENT_PASSWORD);
    }, 450);
  };

  const handleParentPasswordSubmit = () => {
    const pwd = textInputValue.trim();
    if (pwd.length < 6) {
      setErrorMsg("Password must be at least 6 characters.");
      return;
    }
    setErrorMsg("");
    setParentForm((prev) => ({ ...prev, password: pwd }));
    finishEditingIfNeeded();

    addMessage("user", "••••••••", "pingflo", null, STEPS.PARENT_PASSWORD);
    setTextInputValue("");

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskPhone",
          {},
          "Got it! What is your mobile phone number? This is helpful for coach emergency contact during club training sessions. (You can also skip this for now)."
        )
      );
      setCurrentStep(STEPS.PARENT_PHONE);
    }, 450);
  };

  const handleParentPhoneSubmit = async (phoneVal = "") => {
    const phone = phoneVal || textInputValue.trim();
    setParentForm((prev) => ({ ...prev, phone }));
    finishEditingIfNeeded();

    addMessage("user", phone || "Skipped for now", "pingflo", null, STEPS.PARENT_PHONE);
    setTextInputValue("");

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskRelationship",
          {},
          "What is your relationship to the child you are registering?"
        )
      );
      setCurrentStep(STEPS.PARENT_RELATIONSHIP);
    }, 450);
  };

  const handleRelationshipSubmit = (role) => {
    const roleLabels = {
      father: translateMsg("parentWizardRelFather", {}, "Father"),
      mother: translateMsg("parentWizardRelMother", {}, "Mother"),
      tutor: translateMsg("parentWizardRelTutor", {}, "Tutor/Guardian"),
    };
    setParentForm((prev) => ({ ...prev, relationship: role }));
    finishEditingIfNeeded();
    addMessage("user", roleLabels[role] || role, "pingflo", null, STEPS.PARENT_RELATIONSHIP);

    setTimeout(() => {
      continueAfterParentContact();
    }, 450);
  };

  const handleEditExistingChild = (child) => {
    const wizardChild = mapApiChildToWizard(child);
    setCurrentChild(wizardChild);
    setEditingChildPlayerId(child.child_player_id);
    setRegisteredChildren([]);
    if (child.relationship) {
      setParentForm((prev) => ({ ...prev, relationship: child.relationship }));
    }

    const childName = child.first_name || "your child";
    addMessage(
      "user",
      translateMsg(
        "parentWizardEditChild",
        { name: childName },
        `Edit ${childName}`
      )
    );

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardEditChildPrompt",
          { firstName: childName, name: childName },
          `Let's update ${childName}'s profile. You can change their name, birthday, playing style, and consent settings.`
        )
      );
      setCurrentStep(STEPS.CHILD_NAME);
      setTextInputValue([wizardChild.firstName, wizardChild.lastName].filter(Boolean).join(" "));
    }, 450);
  };

  const handleAddNewChildForReturning = () => {
    setEditingChildPlayerId(null);
    setCurrentChild({
      firstName: "",
      lastName: parentForm.lastName || "",
      dateOfBirth: "",
      gender: "boy",
      purpose: "fun",
      experience: "beginner",
      hand: "Right",
      grip: "Shakehand",
      consentVideo: false,
      consentPublic: false,
      consentHr: false,
      guardianAck: false,
      medicalNotes: "",
    });
    setRegisteredChildren([]);

    addMessage(
      "user",
      translateMsg("parentWizardAddNewChild", {}, "Add a new child")
    );

    setTimeout(() => {
      if (!selectedClubIdRef.current && !inviteClubLockedRef.current) {
        addMessage(
          "ai",
          translateMsg(
            "parentWizardAskClub",
            {},
            "Which table tennis club will your child be joining?"
          )
        );
        setCurrentStep(STEPS.CLUB_SELECT);
        return;
      }

      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskChildName",
          {},
          "Wonderful! What is your new child's first name and last name?"
        )
      );
      setCurrentStep(STEPS.CHILD_NAME);
    }, 450);
  };

  const handleClubSelectSubmit = () => {
    const selectedClub = clubs.find((c) => String(c.club_id) === String(selectedClubId));
    const clubLabel = selectedClub ? selectedClub.club_name : "Selected Club";
    const country = selectedClub?.club_country || selectedClub?.country || "";
    const flag = getCountryFlag(country || selectedClub?.country_code);
    const fullClubStr = country ? `${flag} ${clubLabel} (${country})` : `${flag} ${clubLabel}`;
    finishEditingIfNeeded();
    addMessage("user", `Joining: ${fullClubStr}`, "pingflo", null, STEPS.CLUB_SELECT);

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskChildName",
          {},
          `Wonderful! Now let's talk about your young table tennis star! What is your child's first name and last name?`
        )
      );
      setCurrentStep(STEPS.CHILD_NAME);
    }, 450);
  };

  const handleChildNameSubmit = () => {
    const trimmed = textInputValue.trim();
    if (!trimmed) return;
    const parts = trimmed.split(" ");
    const firstName = parts[0] || trimmed;
    const lastName = parts.slice(1).join(" ") || parentForm.lastName;

    setCurrentChild((prev) => ({ ...prev, firstName, lastName }));
    finishEditingIfNeeded();
    addMessage("user", trimmed, "pingflo", null, STEPS.CHILD_NAME);
    setTextInputValue("");

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskDob",
          { firstName, name: firstName },
          `Excited to have ${firstName} on board! When was ${firstName} born? This allows us to tailor the right junior training level and assign the best companion.`
        )
      );
      setCurrentStep(STEPS.CHILD_DOB);
    }, 450);
  };

  const handleChildDobSubmit = (dobValue) => {
    if (!dobValue) return;
    const age = getChildAge(dobValue);
    setCurrentChild((prev) => ({ ...prev, dateOfBirth: dobValue }));
    finishEditingIfNeeded();

    addMessage("user", `${dobValue} (${age} years old)`, "pingflo", null, STEPS.CHILD_DOB);
    const childName = currentChild.firstName || "your child";

    setTimeout(() => {
      if (age <= 12) {
        addMessage(
          "ai",
          translateMsg(
            "parentWizardPingwiIntro",
            { firstName: childName, name: childName },
            `Fantastic! For ${childName}, our joyful junior companion **Pingwi** will turn training into an exciting adventure! Pingwi guides young players through motor agility, story quests, and joyful rally milestones!`
          ),
          "pingwi",
          {
            type: "companion_reveal",
            agentId: "pingwi",
            name: "Pingwi",
            tagline: "Joyful Junior Discovery & Motor Milestones",
          }
        );
      } else {
        addMessage(
          "ai",
          translateMsg(
            "parentWizardPingfloIntro",
            { firstName: childName, name: childName },
            `Awesome! For ${childName}, I will be their club companion, helping them build solid fundamentals, rally consistency, and match confidence!`
          ),
          "pingflo",
          {
            type: "companion_reveal",
            agentId: "pingflo",
            name: "Pingflo",
            tagline: "Guided Technique & Club Companion",
          }
        );
      }

      setTimeout(() => {
        addMessage(
          "ai",
          translateMsg(
            "parentWizardAskMotivation",
            { firstName: childName, name: childName },
            `What is ${childName} most excited about when playing table tennis?`
          )
        );
        setCurrentStep(STEPS.CHILD_MOTIVATION);
      }, 700);
    }, 450);
  };

  const handleMotivationSelect = (purpose, label) => {
    setCurrentChild((prev) => ({ ...prev, purpose }));
    finishEditingIfNeeded();
    addMessage("user", label, "pingflo", null, STEPS.CHILD_MOTIVATION);
    const childName = currentChild.firstName || "your child";

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskExperience",
          { firstName: childName, name: childName },
          `Has ${childName} played table tennis before?`
        )
      );
      setCurrentStep(STEPS.CHILD_EXPERIENCE);
    }, 450);
  };

  const handleExperienceSelect = (exp, label) => {
    setCurrentChild((prev) => ({ ...prev, experience: exp }));
    finishEditingIfNeeded();
    addMessage("user", label, "pingflo", null, STEPS.CHILD_EXPERIENCE);
    const childName = currentChild.firstName || "your child";

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskHandGrip",
          { firstName: childName, name: childName },
          `Which hand does ${childName} play with, and what racket grip do they use?`
        )
      );
      setCurrentStep(STEPS.CHILD_HAND_GRIP);
    }, 450);
  };

  const handleHandGripSubmit = (hand, grip) => {
    setCurrentChild((prev) => ({ ...prev, hand, grip }));
    finishEditingIfNeeded();
    const handLabel =
      hand === "Left"
        ? translateMsg("parentWizardHandLeftWord", {}, "Left")
        : translateMsg("parentWizardHandRightWord", {}, "Right");
    addMessage(
      "user",
      translateMsg(
        "parentWizardHandGripSummary",
        { hand: handLabel, grip: grip || "Shakehand" },
        `${handLabel} hand • ${grip || "Shakehand"} grip`
      ),
      "pingflo",
      null,
      STEPS.CHILD_HAND_GRIP
    );

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardAskConsent",
          {},
          `Almost done! For child safeguarding and GDPR compliance, please review and confirm the parental consent settings below:`
        )
      );
      setCurrentStep(STEPS.PARENT_CONSENT);
    }, 450);
  };

  const handleConsentSubmit = async () => {
    if (!currentChild.guardianAck) {
      setErrorMsg(
        translateMsg(
          "parentWizardConsentAckRequired",
          {},
          "Confirm you are a parent or guardian and that you have read the notice."
        )
      );
      return;
    }
    finishEditingIfNeeded();
    addMessage(
      "user",
      translateMsg(
        "parentWizardConsentVerified",
        {
          mode: [
            currentChild.consentVideo
              ? translateMsg("parentWizardConsentVideoOn", {}, "Coach video granted")
              : translateMsg("parentWizardConsentVideoOff", {}, "No coach video"),
            currentChild.consentPublic
              ? translateMsg("parentWizardConsentPublicOn", {}, "Public image granted")
              : translateMsg("parentWizardConsentPublicOff", {}, "No public image"),
            currentChild.consentHr
              ? translateMsg("parentWizardConsentHrOn", {}, "HR data granted")
              : translateMsg("parentWizardConsentHrOff", {}, "No HR data"),
          ].join(" · "),
        },
        `Consents recorded (${
          currentChild.consentVideo ? "Coach video granted" : "No coach video"
        } · ${
          currentChild.consentPublic ? "Public image granted" : "No public image"
        } · ${currentChild.consentHr ? "HR data granted" : "No HR data"})`
      ),
      "pingflo",
      null,
      STEPS.PARENT_CONSENT
    );

    const childWithRel = {
      ...currentChild,
      relationship: parentForm.relationship || "parent",
    };

    if (editingChildPlayerId && isReturningParent) {
      setLoading(true);
      setErrorMsg("");
      try {
        await axios.put(
          getApiUrl(`/api/parent/child/${editingChildPlayerId}`),
          { childData: buildChildPayload(childWithRel) },
          { headers: getAuthHeaders() }
        );
        addMessage(
          "ai",
          translateMsg(
            "parentWizardComplete",
            {},
            "Profile updated! Opening your Parent Progress Hub..."
          )
        );
        setTimeout(() => {
          navigate("/parent/progress", { replace: true });
        }, 1200);
      } catch (err) {
        console.error("Child update error:", err);
        const msg = err.response?.data?.message || err.message || "An error occurred";
        setErrorMsg(msg);
        addMessage("ai", `⚠️ ${msg}. Please try again.`);
        setLoading(false);
      }
      return;
    }

    const updatedChildren = [...registeredChildren, childWithRel];
    setRegisteredChildren(updatedChildren);
    const childName = currentChild.firstName || "your child";

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardMultiChildPrompt",
          { firstName: childName, name: childName },
          `Awesome! ${childName} is ready to join the club! Do you have another child you'd like to register, or shall we complete registration and enter your Parent Progress Hub?`
        )
      );
      setCurrentStep(STEPS.MULTI_CHILD_CHECK);
    }, 450);
  };

  const handleAddAnotherChild = () => {
    // Reset child form
    setCurrentChild({
      firstName: "",
      lastName: parentForm.lastName || "",
      dateOfBirth: "",
      gender: "boy",
      purpose: "fun",
      experience: "beginner",
      hand: "Right",
      grip: "Shakehand",
      consentVideo: false,
      consentPublic: false,
      consentHr: false,
      guardianAck: false,
      medicalNotes: "",
    });

    addMessage("user", "Register another child");

    setTimeout(() => {
      addMessage(
        "ai",
        translateMsg(
          "parentWizardNextChildName",
          {},
          "Great! What is your other child's first and last name?"
        )
      );
      setCurrentStep(STEPS.CHILD_NAME);
    }, 450);
  };

  const handleCompleteRegistration = async () => {
    setLoading(true);
    setErrorMsg("");
    addMessage("user", "Complete & enter Parent Hub");

    try {
      const childrenToSave =
        registeredChildren.length > 0
          ? registeredChildren
          : [{ ...currentChild, relationship: parentForm.relationship || "parent" }];

      if (isReturningParent && localStorage.getItem("token")) {
        for (const child of childrenToSave) {
          const childData = buildChildPayload(child);
          await axios.post(
            getApiUrl("/api/parent/add-child"),
            { childData },
            { headers: getAuthHeaders() }
          );
        }

        addMessage(
          "ai",
          translateMsg(
            "parentWizardComplete",
            {},
            "🎉 All set! Opening your Parent Progress Hub..."
          )
        );

        navigate("/parent/progress", { replace: true });
        return;
      }

      const payload = {
        parentData: {
          firstName: parentForm.firstName,
          lastName: parentForm.lastName,
          email: parentForm.email,
          password: parentForm.password,
          phone: parentForm.phone,
          relationship: parentForm.relationship || "parent",
        },
        children: childrenToSave.map((child) => ({
          ...child,
          relationship: child.relationship || parentForm.relationship || "parent",
        })),
        clubId: selectedClubId,
        invitationToken: invitationToken || undefined,
      };

      const res = await axios.post(getApiUrl("/api/parent/register"), payload);

      if (res.data?.success && res.data?.token) {
        const selectedClub = clubsRef.current.find(
          (c) => String(c.club_id) === String(selectedClubId)
        );
        const apiUser = res.data.user || {};
        const club_details =
          apiUser.club_details ||
          (selectedClub
            ? {
                club_id: selectedClub.club_id,
                club_name: selectedClub.club_name,
                club_image: selectedClub.club_image || null,
                country: selectedClub.country || selectedClub.club_country || null,
                club_code: selectedClub.club_code || null,
              }
            : null);
        const storedUser = {
          ...apiUser,
          club_id: apiUser.club_id || selectedClubId || null,
          club_details,
          club: club_details
            ? {
                club_id: club_details.club_id,
                club_name: club_details.club_name,
                club_image: club_details.club_image,
              }
            : apiUser.club || null,
        };
        localStorage.setItem("token", res.data.token);
        localStorage.setItem("user", JSON.stringify(storedUser));
        localStorage.setItem("home_active_role", "parent");

        addMessage(
          "ai",
          translateMsg(
            "parentWizardComplete",
            {},
            "🎉 Registration successful! Welcome to the SmartTT family! Opening your Parent Progress Hub..."
          )
        );

        navigate("/parent/progress", { replace: true });
      } else {
        throw new Error(res.data?.message || "Registration failed");
      }
    } catch (err) {
      console.error("Parent registration error:", err);
      const msg = err.response?.data?.message || err.message || "An error occurred during registration";
      setErrorMsg(msg);
      addMessage("ai", `⚠️ ${msg}. Please try again.`);
      setLoading(false);
    }
  };

  const filteredClubs = clubs.filter((c) => {
    if (!clubSearchTerm.trim()) return true;
    const term = clubSearchTerm.toLowerCase();
    const name = (c.club_name || "").toLowerCase();
    const city = (c.club_city || c.city || "").toLowerCase();
    const country = (c.club_country || c.country || "").toLowerCase();
    return name.includes(term) || city.includes(term) || country.includes(term);
  });

  return (
    <>
      {hasInviteToken && !clubsReady ? (
        <div className="invite-splash-screen" aria-busy="true">
          <div className="invite-boot-card">
            <div
              className="invite-splash-logos"
              role="img"
              aria-label={[invitedClubName, "SmartTT Club"].filter(Boolean).join(" · ") || "SmartTT Club"}
            >
              {invitedClubLogo ? (
                <img
                  src={invitedClubLogo}
                  alt={invitedClubName || ""}
                  className="invite-splash-logos__club"
                />
              ) : null}
              <img
                src={smartttDefaultLogo}
                alt="SmartTT Club"
                className="invite-splash-logos__smarttt"
              />
            </div>
            <p className="intro-body-text">
              {invitedClubName || t("parentWizardLoadingClub") || "Loading your club…"}
            </p>
          </div>
        </div>
      ) : null}

    {!(hasInviteToken && !clubsReady) ? (
    <div className="parent-wizard-container">
      {/* Header */}
      <header className="parent-wizard-header">
        <div className="parent-wizard-brand">
          <div className="parent-wizard-logos">
            {invitedClubLogo ? (
              <img src={invitedClubLogo} alt={invitedClubName || ""} className="parent-wizard-club-logo" />
            ) : null}
          </div>
          <div className="parent-wizard-title-group">
            <h2>{invitedClubName || t("parentWizardTitle") || "Parent Onboarding"}</h2>
            <span>{t("parentWizardPoweredBy") || "Powered by Pingflo & Pingwi"}</span>
          </div>
        </div>
        <div className="parent-wizard-badge">
          <span>👨‍👩‍👧‍👦 {t("parentWizardFamilyJourney") || "Family Journey"}</span>
        </div>
      </header>

      {/* Chat scroll area */}
      <main className="parent-wizard-chat" ref={chatScrollRef}>
        {messages.map((m) => (
          <div key={m.id} className={`chat-row ${m.sender}`} data-sender={m.sender}>
            {m.sender === "ai" && (
              <div className="chat-avatar-wrap">
                {(m.agentId || "pingflo") === "pingflo" ? (
                  <DigipingHostMark
                    clubLogoSrc={invitedClubLogo}
                    size={32}
                    agentId="pingflo"
                    title={invitedClubName || "Pingflo"}
                    imgClassName="parent-wizard-pingflo-club"
                  />
                ) : (
                  <AgentMark agentId={m.agentId || "pingflo"} size={32} />
                )}
              </div>
            )}
            <div className={`chat-bubble ${m.sender}`}>
              {m.sender === "user" && m.step && EDITABLE_STEPS.has(m.step) && !(inviteClubLocked && m.step === STEPS.CLUB_SELECT) ? (
                <div className="parent-user-answer-row">
                  <div>{m.text}</div>
                  <button
                    type="button"
                    className="parent-edit-answer-btn"
                    title={t("parentWizardEditAnswer") || "Edit"}
                    aria-label={t("parentWizardEditAnswer") || "Edit"}
                    disabled={loading || currentStep === STEPS.SUBMITTING}
                    onClick={() => beginEditAnswer(m.id, m.step)}
                  >
                    <EditOutlinedIcon fontSize="small" />
                    <span>{t("parentWizardEditAnswer") || "Edit"}</span>
                  </button>
                </div>
              ) : (
                <div>{m.text}</div>
              )}

              {m.extra?.type === "companion_reveal" && (
                <div className="companion-reveal-card">
                  <div className="companion-reveal-icon">
                    {m.extra.agentId === "pingwi" ? "🐧" : "🏓"}
                  </div>
                  <div className="companion-reveal-info">
                    <h4>Meet {m.extra.name}!</h4>
                    <p>{m.extra.tagline}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </main>

      {/* Bottom Interactive Tray */}
      <footer className="parent-wizard-tray">
        <div className="parent-wizard-tray-content">
          {errorMsg && <div style={{ color: "#ef4444", fontSize: "0.82rem" }}>{errorMsg}</div>}
          {editingStep && editingStep === currentStep && (
            <p className="parent-editing-hint">
              {t("parentWizardEditingHint") || "Update your answer below"}
            </p>
          )}

          {/* Step 1: Parent Name */}
          {currentStep === STEPS.PARENT_NAME && (
            <div className="tray-input-row">
              <input
                type="text"
                className="tray-text-input"
                placeholder="e.g. Marie Dupont"
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleParentNameSubmit()}
                autoFocus
              />
              <button
                className="tray-send-btn" aria-label="Next"
                onClick={handleParentNameSubmit}
                disabled={!textInputValue.trim()}
              >
                <span className="tray-send-btn__label">Next</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 2: Parent Email */}
          {currentStep === STEPS.PARENT_EMAIL && (
            <div className="tray-input-row">
              <input
                type="email"
                className="tray-text-input"
                placeholder="e.g. marie.dupont@email.com"
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleParentEmailSubmit()}
                autoFocus
              />
              <button
                className="tray-send-btn" aria-label="Next"
                onClick={handleParentEmailSubmit}
                disabled={!textInputValue.trim()}
              >
                <span className="tray-send-btn__label">Next</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 3: Parent Password */}
          {currentStep === STEPS.PARENT_PASSWORD && (
            <div className="tray-input-row">
              <input
                type="password"
                className="tray-text-input"
                placeholder="Choose a password (min 6 chars)"
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleParentPasswordSubmit()}
                autoFocus
              />
              <button
                className="tray-send-btn" aria-label="Set password"
                onClick={handleParentPasswordSubmit}
                disabled={textInputValue.length < 6}
              >
                <span className="tray-send-btn__label">Set Password</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 4b: Parent Relationship */}
          {currentStep === STEPS.PARENT_RELATIONSHIP && (
            <div className="pill-options-grid relationship-options-grid">
              <div
                className="pill-option-card"
                onClick={() => handleRelationshipSubmit("father")}
              >
                <span className="pill-icon">👨</span>
                <span className="pill-title">
                  {translateMsg("parentWizardRelFather", {}, "Father")}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() => handleRelationshipSubmit("mother")}
              >
                <span className="pill-icon">👩</span>
                <span className="pill-title">
                  {translateMsg("parentWizardRelMother", {}, "Mother")}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() => handleRelationshipSubmit("tutor")}
              >
                <span className="pill-icon">🛡️</span>
                <span className="pill-title">
                  {translateMsg("parentWizardRelTutor", {}, "Tutor/Guardian")}
                </span>
              </div>
            </div>
          )}

          {/* Returning parent: existing children */}
          {currentStep === STEPS.EXISTING_CHILDREN && (
            <div className="existing-children-tray">
              {existingLinkedChildren.map((child) => (
                <button
                  key={child.child_player_id}
                  type="button"
                  className="existing-child-edit-btn"
                  onClick={() => handleEditExistingChild(child)}
                  disabled={loading}
                >
                  <EditOutlinedIcon fontSize="small" />
                  <span>
                    {translateMsg(
                      "parentWizardEditChild",
                      { name: child.first_name || "Child" },
                      `Edit ${child.first_name || "Child"}`
                    )}
                  </span>
                </button>
              ))}
              <button
                type="button"
                className="tray-send-btn existing-child-add-btn"
                onClick={handleAddNewChildForReturning}
                disabled={loading}
              >
                <AddIcon fontSize="small" />
                <span>{translateMsg("parentWizardAddNewChild", {}, "Add a new child")}</span>
              </button>
            </div>
          )}

          {/* Step 4: Parent Phone */}
          {currentStep === STEPS.PARENT_PHONE && (
            <div className="tray-input-row">
              <input
                type="tel"
                className="tray-text-input"
                placeholder="e.g. +33 6 12 34 56 78"
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleParentPhoneSubmit()}
                autoFocus
              />
              <button
                type="button"
                className="tray-send-btn" aria-label="Save or skip phone"
                onClick={() => handleParentPhoneSubmit()}
                style={{ background: textInputValue ? undefined : "rgba(255,255,255,0.15)", color: "#fff" }}
              >
                <span className="tray-send-btn__label">{textInputValue ? "Save Phone" : "Skip"}</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 5: Club Select */}
          {currentStep === STEPS.CLUB_SELECT && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
              <input
                type="text"
                className="tray-text-input"
                placeholder={translateMsg("parentWizardSearchClubPlaceholder", {}, "🔍 Filter clubs by name, city or country...")}
                value={clubSearchTerm}
                onChange={(e) => setClubSearchTerm(e.target.value)}
                style={{ fontSize: "0.9rem", padding: "0.55rem 0.85rem" }}
              />

              <div style={{ maxHeight: "200px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.45rem", paddingRight: "2px" }}>
                {filteredClubs.length === 0 ? (
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", padding: "0.75rem", textAlign: "center" }}>
                    {clubs.length === 0 ? "Loading available clubs..." : "No matching club found"}
                  </div>
                ) : (
                  filteredClubs.map((c) => {
                    const isSelected = String(c.club_id) === String(selectedClubId);
                    const countryName = c.club_country || c.country || "";
                    const flag = getCountryFlag(countryName || c.country_code);
                    return (
                      <div
                        key={c.club_id}
                        onClick={() => setSelectedClubId(c.club_id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0.6rem 0.85rem",
                          borderRadius: "10px",
                          cursor: "pointer",
                          background: isSelected ? "rgba(59, 130, 246, 0.28)" : "rgba(255, 255, 255, 0.05)",
                          border: isSelected ? "1.5px solid #3b82f6" : "1px solid rgba(255, 255, 255, 0.1)",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", textAlign: "left" }}>
                          <span style={{ fontSize: "1.25rem" }}>{flag}</span>
                          <div>
                            <div style={{ fontWeight: 600, color: "#fff", fontSize: "0.92rem" }}>
                              {c.club_name}
                            </div>
                            <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.6)" }}>
                              {[c.club_city, countryName].filter(Boolean).join(" • ")}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircleIcon sx={{ color: "#3b82f6", fontSize: 20 }} />
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <button
                type="button"
                className="tray-send-btn"
                onClick={handleClubSelectSubmit}
                disabled={!selectedClubId}
                style={{ width: "100%", justifyContent: "center" }}
              >
                <span>{translateMsg("parentWizardConfirmClub", {}, "Confirm Club")}</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 6: Child Name */}
          {currentStep === STEPS.CHILD_NAME && (
            <div className="tray-input-row">
              <input
                type="text"
                className="tray-text-input"
                placeholder="e.g. Léo Dupont"
                value={textInputValue}
                onChange={(e) => setTextInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleChildNameSubmit()}
                autoFocus
              />
              <button
                className="tray-send-btn" aria-label="Next"
                onClick={handleChildNameSubmit}
                disabled={!textInputValue.trim()}
              >
                <span className="tray-send-btn__label">Next</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 7: Child DOB */}
          {currentStep === STEPS.CHILD_DOB && (
            <div className="tray-input-row">
              <input
                type="date"
                className="tray-text-input"
                defaultValue="2016-05-15"
                onChange={(e) => setTextInputValue(e.target.value)}
                autoFocus
              />
              <button
                className="tray-send-btn"
                aria-label={translateMsg("parentWizardConfirmBirthday", {}, "Confirm birthday")}
                onClick={() => handleChildDobSubmit(textInputValue || "2016-05-15")}
              >
                <span className="tray-send-btn__label">
                  {translateMsg("parentWizardConfirmBirthday", {}, "Confirm Birthday")}
                </span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 8: Child Motivation */}
          {currentStep === STEPS.CHILD_MOTIVATION && (
            <div className="pill-options-grid">
              <div
                className="pill-option-card"
                onClick={() =>
                  handleMotivationSelect(
                    "fun",
                    translateMsg("parentWizardMotFunChat", {}, "🎈 Fun & Games with friends")
                  )
                }
              >
                <span className="pill-icon">🎈</span>
                <span className="pill-title">
                  {translateMsg("parentWizardMotFunTitle", {}, "Fun & Friends")}
                </span>
                <span className="pill-sub">
                  {translateMsg("parentWizardMotFunSub", {}, "Playful games and smiling on court")}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() =>
                  handleMotivationSelect(
                    "tricks",
                    translateMsg("parentWizardMotTricksChat", {}, "🚀 Cool tricks & technique")
                  )
                }
              >
                <span className="pill-icon">🚀</span>
                <span className="pill-title">
                  {translateMsg("parentWizardMotTricksTitle", {}, "Tricks & Skills")}
                </span>
                <span className="pill-sub">
                  {translateMsg("parentWizardMotTricksSub", {}, "Learning spins, smashes, and rallies")}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() =>
                  handleMotivationSelect(
                    "fitness",
                    translateMsg("parentWizardMotFitnessChat", {}, "🏃 Energy & Fitness")
                  )
                }
              >
                <span className="pill-icon">🏃</span>
                <span className="pill-title">
                  {translateMsg("parentWizardMotFitnessTitle", {}, "Energy & Agility")}
                </span>
                <span className="pill-sub">
                  {translateMsg(
                    "parentWizardMotFitnessSub",
                    {},
                    "Moving fast, coordination & fitness"
                  )}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() =>
                  handleMotivationSelect(
                    "competition",
                    translateMsg("parentWizardMotCompChat", {}, "🏆 Matches & Tournaments")
                  )
                }
              >
                <span className="pill-icon">🏆</span>
                <span className="pill-title">
                  {translateMsg("parentWizardMotCompTitle", {}, "Tournaments")}
                </span>
                <span className="pill-sub">
                  {translateMsg(
                    "parentWizardMotCompSub",
                    {},
                    "Competitive challenge and winning points"
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Step 9: Child Experience */}
          {currentStep === STEPS.CHILD_EXPERIENCE && (
            <div className="pill-options-grid">
              <div
                className="pill-option-card"
                onClick={() =>
                  handleExperienceSelect(
                    "first_time",
                    translateMsg("parentWizardExpFirstChat", {}, "🌱 Brand new beginner")
                  )
                }
              >
                <span className="pill-icon">🌱</span>
                <span className="pill-title">
                  {translateMsg("parentWizardExpFirstTitle", {}, "First Time")}
                </span>
                <span className="pill-sub">
                  {translateMsg(
                    "parentWizardExpFirstSub",
                    {},
                    "Brand new to the game, ready to discover!"
                  )}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() =>
                  handleExperienceSelect(
                    "recreational",
                    translateMsg("parentWizardExpRecChat", {}, "🏓 Played at school / garage")
                  )
                }
              >
                <span className="pill-icon">🏓</span>
                <span className="pill-title">
                  {translateMsg("parentWizardExpRecTitle", {}, "Recreational")}
                </span>
                <span className="pill-sub">
                  {translateMsg("parentWizardExpRecSub", {}, "Can keep short rallies going")}
                </span>
              </div>
              <div
                className="pill-option-card"
                onClick={() =>
                  handleExperienceSelect(
                    "club_experienced",
                    translateMsg("parentWizardExpClubChat", {}, "⭐ 1-2 years training")
                  )
                }
              >
                <span className="pill-icon">⭐</span>
                <span className="pill-title">
                  {translateMsg("parentWizardExpClubTitle", {}, "Club Player")}
                </span>
                <span className="pill-sub">
                  {translateMsg(
                    "parentWizardExpClubSub",
                    {},
                    "Has regular club practice experience"
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Step 10: Playing Hand & Grip */}
          {currentStep === STEPS.CHILD_HAND_GRIP && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  type="button"
                  className={`tray-send-btn ${currentChild.hand === "Right" ? "selected" : ""}`}
                  style={{
                    flex: 1,
                    background: currentChild.hand === "Right" ? "#ffa451" : "rgba(30, 41, 59, 0.7)",
                    color: currentChild.hand === "Right" ? "#0b0f19" : "#fff",
                  }}
                  onClick={() => setCurrentChild((p) => ({ ...p, hand: "Right" }))}
                >
                  ✋ {translateMsg("parentWizardHandRight", {}, "Right-Handed")}
                </button>
                <button
                  type="button"
                  className={`tray-send-btn ${currentChild.hand === "Left" ? "selected" : ""}`}
                  style={{
                    flex: 1,
                    background: currentChild.hand === "Left" ? "#ffa451" : "rgba(30, 41, 59, 0.7)",
                    color: currentChild.hand === "Left" ? "#0b0f19" : "#fff",
                  }}
                  onClick={() => setCurrentChild((p) => ({ ...p, hand: "Left" }))}
                >
                  🤚 {translateMsg("parentWizardHandLeft", {}, "Left-Handed")}
                </button>
              </div>
              <button
                className="tray-send-btn"
                onClick={() => handleHandGripSubmit(currentChild.hand, currentChild.grip)}
                style={{ width: "100%" }}
              >
                <span>{translateMsg("parentWizardContinue", {}, "Continue")}</span>
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 11: Parental Consent */}
          {currentStep === STEPS.PARENT_CONSENT && (
            <div className="consent-card-tray">
              <p className="consent-notice">
                {translateMsg(
                  "parentWizardConsentNotice",
                  { childName: currentChild.firstName || "" },
                  `These choices are for ${currentChild.firstName || "your child"} only. One parent or guardian is enough. Nothing below is pre-selected. Training does not require video, public image, or heart rate. A paper form is not required. You can withdraw later in the Parent Hub. Coach video stays inside the club (about 30 days on the base plan). Public image covers website, social, and newsletters. Heart rate is effort feedback, not a medical record.`
                )}
              </p>

              <label className="consent-toggle-row">
                <input
                  type="checkbox"
                  checked={!!currentChild.guardianAck}
                  onChange={(e) =>
                    setCurrentChild((p) => ({ ...p, guardianAck: e.target.checked }))
                  }
                />
                <span className="consent-label">
                  {translateMsg(
                    "parentWizardConsentTraining",
                    {
                      relationship: getRelationshipLabel(parentForm.relationship).toLowerCase(),
                      childName: currentChild.firstName || "",
                    },
                    `I am a parent or legal guardian of ${currentChild.firstName} and I have read this notice. I agree to their club training account.`
                  )}
                </span>
              </label>

              <label className="consent-toggle-row">
                <input
                  type="checkbox"
                  checked={!!currentChild.consentVideo}
                  onChange={(e) =>
                    setCurrentChild((p) => ({ ...p, consentVideo: e.target.checked }))
                  }
                />
                <span className="consent-label">
                  {translateMsg(
                    "parentWizardConsentVideo",
                    {},
                    "Coach-only training video: I allow video during training so coaches can review technique. Not for the website or social media."
                  )}
                </span>
              </label>

              <label className="consent-toggle-row">
                <input
                  type="checkbox"
                  checked={!!currentChild.consentPublic}
                  onChange={(e) =>
                    setCurrentChild((p) => ({ ...p, consentPublic: e.target.checked }))
                  }
                />
                <span className="consent-label">
                  {translateMsg(
                    "parentWizardConsentPublic",
                    {},
                    "Public image: I allow the club to use photos or video on its website, social networks, or newsletters. Separate from coach-only video."
                  )}
                </span>
              </label>

              <label className="consent-toggle-row">
                <input
                  type="checkbox"
                  checked={!!currentChild.consentHr}
                  onChange={(e) =>
                    setCurrentChild((p) => ({ ...p, consentHr: e.target.checked }))
                  }
                />
                <span className="consent-label">
                  {translateMsg(
                    "parentWizardConsentHr",
                    {},
                    "Heart rate: I allow heart-rate measurements during training for effort feedback. This is not a medical diagnosis. I can withdraw it at any time."
                  )}
                </span>
              </label>

              <input
                type="text"
                className="tray-text-input"
                style={{ fontSize: "0.85rem", padding: "0.5rem 0.75rem" }}
                placeholder={translateMsg(
                  "parentWizardMedicalNotesPlaceholder",
                  {},
                  "Optional: Medical notes (e.g. asthma, allergies)"
                )}
                value={currentChild.medicalNotes}
                onChange={(e) =>
                  setCurrentChild((p) => ({ ...p, medicalNotes: e.target.value }))
                }
              />

              <button
                className="tray-send-btn"
                onClick={handleConsentSubmit}
                disabled={loading}
                style={{ width: "100%", marginTop: "0.4rem" }}
              >
                <span>
                  {loading
                    ? translateMsg("parentWizardSaving", {}, "Saving...")
                    : editingChildPlayerId
                      ? translateMsg("parentWizardSaveProfileChanges", {}, "Save Profile Changes")
                      : translateMsg(
                          "parentWizardConfirmSaveChild",
                          {},
                          "Confirm & Save Child Profile"
                        )}
                </span>
                <CheckCircleIcon fontSize="small" />
              </button>
            </div>
          )}

          {/* Step 12: Multi-child check */}
          {currentStep === STEPS.MULTI_CHILD_CHECK && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <div className="multi-child-banner">
                <span>
                  {translateMsg("parentWizardChildrenRegistered", {}, "Children registered")}:{" "}
                  <strong>{registeredChildren.map((c) => c.firstName).join(", ")}</strong>
                </span>
                <span>
                  👨‍👩‍👦 {registeredChildren.length}{" "}
                  {registeredChildren.length === 1
                    ? translateMsg("parentWizardChildSingular", {}, "child")
                    : translateMsg("parentWizardChildPlural", {}, "children")}
                </span>
              </div>

              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  type="button"
                  className="tray-send-btn"
                  onClick={handleAddAnotherChild}
                  style={{
                    flex: 1,
                    background: "rgba(30, 41, 59, 0.9)",
                    color: "#f8fafc",
                    border: "1px solid rgba(255,255,255,0.15)",
                  }}
                >
                  <AddIcon fontSize="small" />
                  <span>{translateMsg("parentWizardAddAnotherChild", {}, "Add Another Child")}</span>
                </button>

                <button
                  type="button"
                  className="tray-send-btn"
                  onClick={handleCompleteRegistration}
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  <span>
                    {loading
                      ? translateMsg("parentWizardCreating", {}, "Creating...")
                      : translateMsg("goToDashboard", {}, "Go to dashboard")}
                  </span>
                  <ArrowForwardIcon fontSize="small" />
                </button>
              </div>
            </div>
          )}
        </div>
      </footer>
    </div>
    ) : null}
    </>
  );
}
